import type { BulkApproveResult, WorkItemStatus } from './workflow.js';

/**
 * Wire types for `POST /api/v1/queue/bulk-approve` — the server-side bulk approve that replaced the
 * client-side fan-out. The fan-out fired one POST per item through `Promise.allSettled`; production
 * throttles mutations at 30/min and `postJson` has no 429 retry, so a 539-item approval would have
 * had most of its requests rejected and approved an arbitrary subset. One request now costs one slot.
 *
 * Mirrors `packages/api/src/workflow/bulk-approve.types.ts`. Keep the two in step.
 */

/**
 * A declared atomic group: patches that must all apply or none, because applying part leaves a
 * lesson internally self-contradictory. An independent item is a group of one.
 */
export interface BulkApproveGroupInput {
  groupId: string;
  /** The COMPLETE frozen roster, declared so the server can validate closure. */
  workItemIds: string[];
  /**
   * The members the caller actually reviewed and intends to approve. Optional; absent means "all of
   * them", which is the original behaviour. When present the server approves ONLY these and blocks
   * the group if any other roster member is still awaiting a decision — so a caller declaring a
   * roster it did not read cannot have those siblings approved under its identity.
   */
  approveWorkItemIds?: string[];
}

export type BulkApproveMode = 'preflight' | 'apply';

export type BulkItemOutcome =
  | 'approved'
  /** Written, but its review event and/or audit row may be missing. Live, not refused. */
  | 'approved_trail_incomplete'
  | 'already_approved'
  | 'eligible'
  | 'not_found'
  | 'wrong_status'
  | 'render_blocked'
  | 'answer_gate_blocked'
  | 'mark_gate_blocked'
  | 'no_second_try_blocked'
  /** The declared group disagrees with the roster frozen on the item at import. */
  | 'group_mismatch'
  /** Awaiting a decision, and the caller did not declare it as reviewed. Blocks its group. */
  | 'outside_review_intent'
  /** Ships under a declared machine decision; a person cannot approve it (lane SV, spec S1). Blocks its group. */
  | 'machine_owned'
  | 'skipped_group_blocked'
  | 'refused'
  | 'not_attempted';

/** `partial` is the loud one: the group got split and needs a human before the wave is composed. */
export type BulkGroupOutcome =
  | 'approved'
  | 'already_approved'
  | 'eligible'
  | 'blocked'
  | 'partial'
  | 'refused'
  | 'not_attempted';

export interface BulkApproveItemReport {
  workItemId: string;
  outcome: BulkItemOutcome;
  status?: WorkItemStatus;
  reason?: string;
}

export interface BulkApproveGroupReport {
  groupId: string;
  outcome: BulkGroupOutcome;
  items: BulkApproveItemReport[];
}

export interface BulkApproveReport {
  mode: BulkApproveMode;
  batchId?: string;
  totals: {
    requestedItems: number;
    requestedGroups: number;
    approved: number;
    approvedTrailIncomplete: number;
    alreadyApproved: number;
    /** GROUP counts — the fields above count items. */
    eligibleGroups: number;
    blockedGroups: number;
    partialGroups: number;
    refused: number;
    notAttempted: number;
  };
  stoppedEarly: boolean;
  stoppedReason?: string;
  groups: BulkApproveGroupReport[];
}

/**
 * The server report flattened into the shape the queue UI already speaks, without losing anything:
 * `succeeded` / `failed` drive the existing list updates, `alreadyApproved` keeps a re-run from
 * being reported as fresh approvals, and `report` is the full per-item detail.
 */
export interface BulkApproveOutcome extends BulkApproveResult {
  alreadyApproved: string[];
  /**
   * Approved, but the write that followed failed — the review event and/or audit row may be
   * missing. Also present in `succeeded`, because the item really did transition and the queue list
   * must move it; listed separately so the banner can refuse to call the run clean.
   */
  trailIncomplete: string[];
  report: BulkApproveReport;
}

/** Item outcomes that mean the item is NOT approved and the operator has to do something. */
const FAILURE_OUTCOMES = new Set<BulkItemOutcome>([
  'not_found',
  'wrong_status',
  'render_blocked',
  'answer_gate_blocked',
  'mark_gate_blocked',
  'no_second_try_blocked',
  'group_mismatch',
  'outside_review_intent',
  'machine_owned',
  'skipped_group_blocked',
  'refused',
  'not_attempted',
]);

/** Flatten a server report into the queue UI's `{succeeded, failed}` shape. */
export function toBulkApproveOutcome(report: BulkApproveReport): BulkApproveOutcome {
  const items = report.groups.flatMap((group) => group.items);
  return {
    succeeded: items
      .filter((item) => item.outcome === 'approved' || item.outcome === 'approved_trail_incomplete')
      .map((item) => item.workItemId),
    trailIncomplete: items
      .filter((item) => item.outcome === 'approved_trail_incomplete')
      .map((item) => item.workItemId),
    alreadyApproved: items.filter((item) => item.outcome === 'already_approved').map((item) => item.workItemId),
    failed: items
      .filter((item) => FAILURE_OUTCOMES.has(item.outcome))
      .map((item) => ({
        workItemId: item.workItemId,
        error: item.reason ?? describeOutcome(item.outcome),
      })),
    report,
  };
}

function describeOutcome(outcome: BulkItemOutcome): string {
  switch (outcome) {
    case 'not_found':
      return 'No such work item';
    case 'wrong_status':
      return 'Not in an approvable status';
    case 'render_blocked':
      return 'Blocking render-safety issues — approve this one on its own screen';
    case 'answer_gate_blocked':
      return 'Answer gate does not allow this approval decision';
    case 'mark_gate_blocked':
      return 'Answer-mark gate does not allow this approval decision';
    case 'no_second_try_blocked':
      return 'One-attempt conversion approval refused by the server';
    case 'group_mismatch':
      return 'Belongs to an atomic group whose other members were not submitted with it';
    case 'outside_review_intent':
      return 'Still awaiting review, and its all-or-nothing group cannot be approved without it';
    case 'machine_owned':
      return 'Not in the review queue — it cannot be approved from here';
    case 'skipped_group_blocked':
      return 'Left untouched because another member of its atomic group could not be approved';
    case 'not_attempted':
      return 'Not attempted — re-run to continue';
    default:
      return 'Refused';
  }
}
